/**
 * Link de convite para profissionais (A037).
 *
 * Um convite por e-mail é para UMA pessoa que a marca escolheu. O link é o
 * contrário: a marca publica um endereço e quem chegar por ele pede para
 * entrar. É o que uma clínica com trinta profissionais quer em vez de trinta
 * e-mails — e é também um risco que o convite não tinha: o link vaza.
 *
 * Por isso ele carrega, além do token, duas escolhas da marca:
 *
 * - `expiresAt` — quando deixa de valer; `null` é "nunca";
 * - `requerValidacao` — se quem entrar por ele fica aguardando aprovação de
 *   uma administradora antes de atender.
 *
 * Qualquer combinação vale, inclusive "nunca expira" + "sem validação". A
 * versão original travava essa combinação (vazado uma vez, o link criaria
 * profissionais para sempre), mas há marcas cujas profissionais só veem
 * conteúdo e não atendem — para elas a trava era rigor demais. O risco fica
 * contido onde ele existe: profissional que entrou sem validação é sinalizada
 * na lista, pode ser validada depois, e enquanto houver uma assim a rede da
 * marca não participa de concessão (ver `redeEstaValidada`).
 *
 * Só um link ativo por marca: gerar de novo revoga o anterior.
 *
 * O token fica gravado (além do hash, que é a chave de busca). O desenho
 * original guardava só o hash, como o convite por e-mail — mas o convite é
 * a credencial de UMA pessoa, e o link é um endereço que a marca publica e
 * qualquer administradora dela vê na tela. Esconder do banco o que a tela
 * mostra não protegia nada e custava o uso real: quem reabria a modal não
 * via mais o link nem o QR, e regenerava — invalidando o que já tinha sido
 * impresso ou enviado.
 */
import { z } from 'zod';
import { brandIdField } from './brand';

export const BrandInviteLinkStatusEnum = z.enum(['ACTIVE', 'REVOKED']);

export const BrandInviteLinkSchema = z.object({
  id: z.string(),
  brandId: brandIdField(),
  /** SHA-256 do token — a chave pela qual a rota pública encontra o link. */
  tokenHash: z.string(),
  /**
   * O token em claro, para a tela da marca mostrar o link e o QR a qualquer
   * momento. Ausente em link gerado antes disto: a tela pede para regenerar.
   */
  token: z.string().optional(),
  /** `null` = nunca expira. */
  expiresAt: z.coerce.date().nullable(),
  requerValidacao: z.boolean(),
  status: BrandInviteLinkStatusEnum.default('ACTIVE'),
  /** Quantas vezes a tela do link foi aberta. É o número que diz "vazou?". */
  acessos: z.number().int().nonnegative().default(0),
  /** Quantos pedidos de convite saíram dele. */
  cadastros: z.number().int().nonnegative().default(0),
  createdByAccountId: z.string(),
  revokedAt: z.coerce.date().nullable().default(null),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type BrandInviteLink = z.infer<typeof BrandInviteLinkSchema>;

/** As durações que a tela oferece, em horas. `null` é "nunca". */
export const DURACOES_DO_LINK: ReadonlyArray<{ horas: number | null; rotulo: string }> = [
  { horas: 24, rotulo: '24 horas' },
  { horas: 48, rotulo: '48 horas' },
  { horas: 24 * 7, rotulo: '7 dias' },
  { horas: 24 * 30, rotulo: '30 dias' },
  { horas: null, rotulo: 'Nunca' },
];

export const DURACAO_PADRAO_DO_LINK_H = 48;

/**
 * As opções que a marca pode escolher. Devolve a lista de erros — vazia é
 * válido. Vale na tela e no servidor, de propósito: a mesma função.
 */
export function validarOpcoesDoLink(opcoes: { horas: number | null; requerValidacao: boolean }): string[] {
  const erros: string[] = [];
  const conhecida = DURACOES_DO_LINK.some((d) => d.horas === opcoes.horas);
  if (!conhecida) erros.push('Duração desconhecida.');
  return erros;
}

/** O link ainda abre a porta? */
export function linkEstaValido(link: Pick<BrandInviteLink, 'status' | 'expiresAt'>, agora = new Date()): boolean {
  if (link.status !== 'ACTIVE') return false;
  if (link.expiresAt && link.expiresAt.getTime() <= agora.getTime()) return false;
  return true;
}
